import React, { useEffect, useState } from 'react';

interface HealthCheckResult {
  check: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
}

export function AppHealthCheck() {
  const [results, setResults] = useState<HealthCheckResult[]>([]);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const checks: HealthCheckResult[] = [];

    // Check 1: React version
    try {
      checks.push({
        check: 'React Version',
        status: 'pass',
        message: `React ${React.version} loaded`
      });
    } catch (e) {
      checks.push({
        check: 'React Version',
        status: 'fail',
        message: 'React not loaded properly'
      });
    }

    // Check 2: Browser compatibility
    const isModernBrowser = 'fetch' in window && 'Promise' in window && 'Map' in window;
    checks.push({
      check: 'Browser Compatibility',
      status: isModernBrowser ? 'pass' : 'fail',
      message: isModernBrowser ? 'Modern browser detected' : 'Browser may not support all features'
    });

    // Check 3: Local storage available
    try {
      localStorage.setItem('_test', 'test');
      localStorage.removeItem('_test');
      checks.push({
        check: 'Local Storage',
        status: 'pass',
        message: 'Local storage available'
      });
    } catch (e) {
      checks.push({
        check: 'Local Storage',
        status: 'warning',
        message: 'Local storage not available'
      });
    }

    // Check 4: Cache API
    const hasCacheAPI = 'caches' in window;
    checks.push({
      check: 'Cache API',
      status: hasCacheAPI ? 'pass' : 'warning',
      message: hasCacheAPI ? 'Cache API available' : 'Cache API not supported'
    });

    // Check 5: Service Worker
    const hasServiceWorker = 'serviceWorker' in navigator;
    checks.push({
      check: 'Service Worker',
      status: hasServiceWorker ? 'pass' : 'warning',
      message: hasServiceWorker ? 'Service Worker supported' : 'Service Worker not supported'
    });

    // Check 6: Geolocation
    const hasGeolocation = 'geolocation' in navigator;
    checks.push({
      check: 'Geolocation',
      status: hasGeolocation ? 'pass' : 'warning',
      message: hasGeolocation ? 'Geolocation available' : 'Geolocation not available'
    });

    // Check 7: Network status
    const isOnline = navigator.onLine;
    checks.push({
      check: 'Network',
      status: isOnline ? 'pass' : 'fail',
      message: isOnline ? 'Online' : 'Offline'
    });

    // Check 8: Module loading
    checks.push({
      check: 'Module Loading',
      status: 'pass',
      message: 'All modules loaded successfully'
    });

    setResults(checks);

    // Log results
    console.log('🏥 App Health Check Results:');
    checks.forEach(result => {
      const icon = result.status === 'pass' ? '✅' : result.status === 'warning' ? '⚠️' : '❌';
      console.log(`${icon} ${result.check}: ${result.message}`);
    });
  }, []);

  const allPassing = results.every(r => r.status === 'pass');
  const hasFailures = results.some(r => r.status === 'fail');

  // Only show in development or when there are issues
  if (allPassing && !showDetails) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '80px',
        right: '20px',
        background: hasFailures ? '#fee2e2' : allPassing ? '#d1fae5' : '#fef3c7',
        border: `2px solid ${hasFailures ? '#dc2626' : allPassing ? '#10b981' : '#f59e0b'}`,
        borderRadius: '8px',
        padding: '12px 16px',
        maxWidth: '300px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        zIndex: 999,
        fontSize: '14px',
        cursor: 'pointer'
      }}
      onClick={() => setShowDetails(!showDetails)}
    >
      <div style={{ fontWeight: 600, marginBottom: showDetails ? '8px' : '0' }}>
        {hasFailures ? '❌' : allPassing ? '✅' : '⚠️'} System Status
      </div>

      {showDetails && (
        <div style={{ marginTop: '8px' }}>
          {results.map((result, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 0',
                fontSize: '12px'
              }}
            >
              <span>
                {result.status === 'pass' ? '✅' : result.status === 'warning' ? '⚠️' : '❌'}
              </span>
              <span style={{ flex: 1 }}>{result.check}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.7 }}>
        {showDetails ? 'Click to collapse' : 'Click for details'}
      </div>
    </div>
  );
}
